import { Input, Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { TextNumericControl } from "../controls/text_numeric"
import { numericDataSocket, numericNumberDataSocket } from "../socket_types"
import { NumericTileGrid } from "../tile_grid"
import { NumericConstant } from "../numeric_constant"
import { BaseComponent } from "./base_component"

const DATA_INPUT_KEY = "in"
const MIN_INPUT_KEY = "min"
const MAX_INPUT_KEY = "max"
const MIN_DATA_KEY = "minValue"
const MAX_DATA_KEY = "maxValue"

function parseNumeric(value: unknown): number {
  if (typeof value === "number") return value
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value)
    return Number.isNaN(parsed) ? NaN : parsed
  }
  return NaN
}

export class ClipComponent extends BaseComponent {
  constructor() {
    super("Clip")
    this.category = "Arithmetic"
  }

  async builder(node: Node) {
    const minInput = new Input(MIN_INPUT_KEY, "Min", numericNumberDataSocket)
    const minControl = new TextNumericControl(
      this.editor,
      "_minCaption",
      MIN_DATA_KEY,
      "Min",
      "Numeric input",
      "",
      "",
      true
    )
    ;(minControl as any).key = "Min"
    minInput.addControl(minControl)

    const maxInput = new Input(MAX_INPUT_KEY, "Max", numericNumberDataSocket)
    const maxControl = new TextNumericControl(
      this.editor,
      "_maxCaption",
      MAX_DATA_KEY,
      "Max",
      "Numeric input",
      "",
      "",
      true
    )
    ;(maxControl as any).key = "Max"
    maxInput.addControl(maxControl)

    node.addInput(new Input(DATA_INPUT_KEY, "Numeric dataset", numericDataSocket))
    node.addInput(minInput)
    node.addInput(maxInput)
    node.addOutput(new Output("out", "Clipped dataset", numericDataSocket))
  }

  worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs) {
    const editorNode = this.editor?.nodes.find(n => n.id === node.id)
    if (!editorNode) return

    const inputGrid = inputs[DATA_INPUT_KEY]?.[0]
    if (!(inputGrid instanceof NumericTileGrid)) {
      editorNode.meta.errorMessage = "Numeric dataset input required"
      editorNode.update()
      return
    }

    const minSocket = inputs[MIN_INPUT_KEY]?.[0]
    const maxSocket = inputs[MAX_INPUT_KEY]?.[0]

    const minFromNode = parseNumeric(editorNode.data[MIN_DATA_KEY])
    const maxFromNode = parseNumeric(editorNode.data[MAX_DATA_KEY])

    const out = new NumericTileGrid(inputGrid.zoom, inputGrid.x, inputGrid.y, inputGrid.width, inputGrid.height, NaN)

    inputGrid.iterate((x, y, value) => {
      const min =
        minSocket instanceof NumericTileGrid
          ? minSocket.get(x, y, inputGrid.zoom)
          : minSocket instanceof NumericConstant
            ? minSocket.value
            : typeof minSocket === "number"
              ? minSocket
              : minFromNode

      const max =
        maxSocket instanceof NumericTileGrid
          ? maxSocket.get(x, y, inputGrid.zoom)
          : maxSocket instanceof NumericConstant
            ? maxSocket.value
            : typeof maxSocket === "number"
              ? maxSocket
              : maxFromNode

      if (Number.isNaN(value)) {
        out.set(x, y, NaN)
        return
      }

      const effectiveMin = Number.isNaN(min) ? -Infinity : min
      const effectiveMax = Number.isNaN(max) ? Infinity : max
      const lo = Math.min(effectiveMin, effectiveMax)
      const hi = Math.max(effectiveMin, effectiveMax)

      out.set(x, y, Math.min(Math.max(value, lo), hi))
    })

    out.name = ((editorNode.data.name as string) || "Clip") + " data"
    outputs["out"] = out
    delete editorNode.meta.errorMessage
    editorNode.update()
  }
}
